import { useState } from 'react';

import { ITEM_CATALOG, PLANS, TIER_ORDER, type PlanTier } from '@dca/shared';

import { DataTable } from '../components/DataTable.tsx';
import { Badge, Card, Empty, Notice, Spinner, StatTile } from '../components/Ui.tsx';
import { api } from '../lib/api.ts';
import { useAsync, useAuth } from '../lib/auth.tsx';
import { money, number, relativeTime, unitMoney } from '../lib/format.ts';
import { ErrorState } from './Upgrade.tsx';

export function AdminPage() {
  const { me } = useAuth();
  const overview = useAsync(() => api.admin.overview(), []);
  const coverage = useAsync(() => api.admin.coverage(), []);
  const baselines = useAsync(() => api.market.baselines(), []);
  const marketStats = useAsync(() => api.market.stats(), []);
  const subscribers = useAsync(() => api.admin.subscribers(), []);
  const flooders = useAsync(() => api.admin.flooders(), []);
  const [running, setRunning] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  if (!me?.subscriber.isAdmin) {
    return <Empty title="Admin access required" />;
  }
  if (overview.loading) return <Spinner />;
  if (overview.error) return <ErrorState error={overview.error} />;

  const stats = overview.data!;

  const run = async (name: string, job: () => Promise<unknown>) => {
    setRunning(name);
    setMessage(null);
    try {
      const result = await job();
      setMessage(`${name}: ${JSON.stringify(result)}`);
      overview.reload();
      coverage.reload();
    } catch (error) {
      setMessage(`${name} failed: ${error instanceof Error ? error.message : 'unknown error'}`);
    } finally {
      setRunning(null);
    }
  };

  return (
    <>
      <header className="page-head">
        <div>
          <h1>Admin console</h1>
          <p>Network health, data quality and the manual job triggers.</p>
        </div>
        <div className="toolbar">
          <button
            disabled={running != null}
            onClick={() => run('rebuild benchmarks', api.admin.rebuildBenchmarks)}
          >
            Rebuild benchmarks
          </button>
          <button
            disabled={running != null}
            onClick={() => run('evaluate alerts', api.admin.evaluateAlerts)}
          >
            Evaluate alerts
          </button>
          <button disabled={running != null} onClick={() => run('billing', api.admin.runBilling)}>
            Run billing
          </button>
        </div>
      </header>

      {message && (
        <div style={{ marginBottom: 16 }}>
          <Notice>
            <code>{message}</code>
          </Notice>
        </div>
      )}

      <div className="grid tiles">
        <StatTile
          label="Subscribers"
          value={number(stats.subscribers)}
          foot={
            <span className="muted">
              {Object.entries(stats.subscribersByTier)
                .map(([tier, count]) => `${count} ${tier}`)
                .join(', ')}
            </span>
          }
        />
        <StatTile
          label="Verified connections"
          value={`${number(stats.verifiedConnections)} / ${number(stats.connections)}`}
          foot={<span className="muted">webhooks confirmed end to end</span>}
        />
        <StatTile
          label="Transaction events"
          value={number(stats.transactionEvents)}
          foot={<span className="muted">{number(stats.ingestedLast24h)} in the last 24h</span>}
        />
        <StatTile
          label="Needs attention"
          value={number(stats.deadLetters + stats.unmatchedBillingEvents)}
          foot={
            <span className="muted">
              {number(stats.deadLetters)} dead letters, {number(stats.unmatchedBillingEvents)}{' '}
              unmatched payments
            </span>
          }
        />
      </div>

      {marketStats.data?.stats && (
        <Card
          title="Live ChestShop market"
          subtitle={`Public /chestshop/stats endpoint, cached ${relativeTime(marketStats.data.stats.fetchedAt)}`}
        >
          <div className="grid tiles">
            <StatTile label="Active shops" value={number(marketStats.data.stats.activeShops)} />
            <StatTile label="Distinct items" value={number(marketStats.data.stats.distinctItems)} />
            <StatTile label="Total sales" value={number(marketStats.data.stats.totalSales)} />
            <StatTile
              label="Total volume"
              value={`$${Number(marketStats.data.stats.totalVolume).toLocaleString('en-US', { maximumFractionDigits: 0 })}`}
            />
          </div>
        </Card>
      )}

      <SubscriberCard subscribers={subscribers} />
      <FlooderCard flooders={flooders} />
      <CoverageCard coverage={coverage} />
      <BaselineCard baselines={baselines} />
    </>
  );
}

type FlooderResult = ReturnType<typeof useAsync<Awaited<ReturnType<typeof api.admin.flooders>>>>;

/**
 * Owners whose duplicate, never-selling signs are hidden from the market data.
 *
 * A manual list on purpose. Running many chests is not by itself dishonest,
 * and quietly suppressing an honest seller's cheap listings would be a worse
 * fault than the one it fixes - so a person names them, and can un-name them.
 */
function FlooderCard({ flooders }: { flooders: FlooderResult }) {
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const rows = flooders.data?.flooders ?? [];

  const add = async () => {
    const owner = name.trim();
    if (!owner) return;
    setBusy(true);
    setNote(null);
    try {
      const result = await api.admin.addFlooder(owner);
      setNote(
        `${owner}: ${result.run.suppressed} of ${result.listed} signs suppressed, ` +
          `${result.run.keptSelling} kept because they sell, ` +
          `${result.run.skippedUnwatched} not watched enough to judge.`,
      );
      setName('');
      flooders.reload();
    } catch (error) {
      setNote(error instanceof Error ? error.message : 'Could not add that owner.');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (owner: string) => {
    setBusy(true);
    setNote(null);
    try {
      const result = await api.admin.removeFlooder(owner);
      setNote(`${owner} removed - ${result.released} signs released back into the market data.`);
      flooders.reload();
    } finally {
      setBusy(false);
    }
  };

  const sweep = async () => {
    setBusy(true);
    setNote(null);
    try {
      const result = await api.admin.sweepFlooders();
      const total = result.runs.reduce((sum, r) => sum + Number(r.suppressed ?? 0), 0);
      setNote(`Swept ${result.runs.length} owners, ${total} signs suppressed.`);
      flooders.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Market flooders"
      subtitle="Hopper-chained decoy chests, excluded from prices and from where-to-buy"
      actions={
        <button className="ghost" onClick={sweep} disabled={busy || rows.length === 0}>
          Re-sweep all
        </button>
      }
    >
      <div className="row" style={{ marginBottom: 14, alignItems: 'flex-end' }}>
        <div className="field" style={{ marginBottom: 0, minWidth: 240 }}>
          <label htmlFor="flooder">In-game owner name</label>
          <input
            id="flooder"
            value={name}
            placeholder="e.g. Vernicia"
            onChange={(event) => setName(event.target.value)}
            onKeyDown={(event) => event.key === 'Enter' && add()}
          />
        </div>
        <button className="primary" onClick={add} disabled={busy || name.trim().length < 2}>
          {busy ? 'Working...' : 'Add and sweep'}
        </button>
      </div>

      {note && (
        <div style={{ marginBottom: 12 }}>
          <Notice>{note}</Notice>
        </div>
      )}

      {rows.length === 0 ? (
        <Empty title="Nobody listed">
          Signs are only suppressed for owners named here. Nothing is inferred.
        </Empty>
      ) : (
        <DataTable
          rows={rows}
          rowKey={(row) => row.ownerName}
          columns={[
            {
              key: 'owner',
              header: 'Owner',
              sortValue: (row) => row.ownerName,
              render: (row) => <strong>{row.ownerName}</strong>,
            },
            {
              key: 'suppressed',
              header: 'Signs suppressed',
              align: 'num',
              sortValue: (row) => row.suppressedSigns,
              render: (row) => number(row.suppressedSigns),
            },
            {
              key: 'added',
              header: 'Listed',
              align: 'num',
              sortValue: (row) => row.createdAt,
              render: (row) => <span className="muted small">{relativeTime(row.createdAt)}</span>,
            },
            {
              key: 'actions',
              header: '',
              width: '96px',
              render: (row) => (
                <button className="ghost" disabled={busy} onClick={() => remove(row.ownerName)}>
                  Remove
                </button>
              ),
            },
          ]}
        />
      )}
    </Card>
  );
}

type SubscriberResult = ReturnType<
  typeof useAsync<Awaited<ReturnType<typeof api.admin.subscribers>>>
>;

/**
 * Everyone who has signed up.
 *
 * The endpoint for this existed from the start but nothing ever rendered it,
 * so the only visible figure was a count on a stat tile - which told an
 * operator that people had registered but not who, nor whether their shops
 * had connected.
 */
function SubscriberCard({ subscribers }: { subscribers: SubscriberResult }) {
  const [busy, setBusy] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  if (subscribers.loading) {
    return (
      <Card title="Subscribers">
        <Spinner />
      </Card>
    );
  }
  const rows = subscribers.data?.subscribers ?? [];

  const changeTier = async (id: string, email: string, tier: PlanTier) => {
    setBusy(id);
    setNote(null);
    try {
      await api.admin.setTier(id, tier);
      setNote(`${email} is now on ${PLANS[tier].name}.`);
      subscribers.reload();
    } catch (error) {
      setNote(error instanceof Error ? error.message : 'Could not change that plan.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <Card title={`Subscribers (${number(rows.length)})`} subtitle="Newest first">
      {note && (
        <div style={{ marginBottom: 12 }}>
          <Notice>{note}</Notice>
        </div>
      )}
      {rows.length === 0 ? (
        <Empty title="Nobody has signed up yet" />
      ) : (
        <DataTable
          rows={rows}
          rowKey={(row) => row.id}
          searchPlaceholder="Search by email"
          pageSize={50}
          columns={[
            {
              key: 'email',
              header: 'Account',
              searchText: (row) => `${row.email} ${row.role}`,
              sortValue: (row) => row.email,
              render: (row) => (
                <span>
                  <button
                    className="ghost link-like"
                    onClick={() => setOpenId(openId === row.id ? null : row.id)}
                    title="Show connections and chestshops"
                  >
                    <strong>{row.email}</strong>
                  </button>
                  <div className="small muted">
                    {row.role.replace('_', ' ')}
                    {row.isAdmin && <span className="badge critical" style={{ marginLeft: 6 }}>admin</span>}
                  </div>
                </span>
              ),
            },
            {
              key: 'tier',
              header: 'Plan',
              sortValue: (row) => TIER_ORDER.indexOf(row.tier),
              render: (row) => (
                <select
                  value={row.tier}
                  disabled={busy != null}
                  onChange={(event) => changeTier(row.id, row.email, event.target.value as PlanTier)}
                >
                  {TIER_ORDER.map((tier) => (
                    <option key={tier} value={tier}>
                      {PLANS[tier].name}
                    </option>
                  ))}
                </select>
              ),
            },
            {
              key: 'shops',
              header: 'Connections',
              align: 'num',
              sortValue: (row) => row.connections,
              render: (row) => (
                <span>
                  {number(row.connections)}
                  {row.connections > 0 && (
                    <div className="small muted">{number(row.verified)} verified</div>
                  )}
                </span>
              ),
            },
            {
              key: 'joined',
              header: 'Joined',
              align: 'num',
              sortValue: (row) => row.createdAt,
              render: (row) => <span className="muted small">{relativeTime(row.createdAt)}</span>,
            },
            {
              key: 'last',
              header: 'Last event',
              align: 'num',
              sortValue: (row) => row.lastEventAt,
              render: (row) =>
                row.lastEventAt ? (
                  <span className="muted small">{relativeTime(row.lastEventAt)}</span>
                ) : (
                  <span className="muted small">never</span>
                ),
            },
          ]}
        />
      )}
      {openId && <SubscriberDetail id={openId} onClose={() => setOpenId(null)} />}
    </Card>
  );
}

/**
 * Everything one account actually has: its connections, and every chestshop
 * those connections own. The list view says who exists; this says what is
 * going on with them, which is what a support question needs.
 */
function SubscriberDetail({ id, onClose }: { id: string; onClose: () => void }) {
  const detail = useAsync(() => api.admin.subscriberDetail(id), [id]);

  if (detail.loading) return <Spinner />;
  const data = detail.data;
  if (!data) return null;

  return (
    <div className="subscriber-detail">
      <div className="spread" style={{ marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>{data.subscriber.email}</h3>
        <button className="ghost" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="grid tiles">
        <StatTile label="Chestshops" value={number(data.totals.signs)} />
        <StatTile label="Distinct items" value={number(data.totals.items)} />
        <StatTile label="Stock value" value={money(data.totals.stockValue)} />
        <StatTile label="Connections" value={number(data.connections.length)} />
      </div>

      {data.connections.length === 0 ? (
        <Notice tone="warn">
          No Minecraft account connected, so none of their shops can be identified.
        </Notice>
      ) : (
        <div style={{ marginBottom: 14 }}>
          {data.connections.map((c) => (
            <div key={c.id} className="row small" style={{ gap: 12 }}>
              <strong>{c.ownerName ?? c.dcAccountCode}</strong>
              <Badge tone={c.webhookStatus === 'verified' ? 'good' : 'muted'}>
                {c.webhookStatus}
              </Badge>
              <span className="muted">
                {c.accountType} - {number(c.eventCount)} events
                {c.lastEventAt ? `, last ${relativeTime(c.lastEventAt)}` : ', none received'}
              </span>
            </div>
          ))}
        </div>
      )}

      {data.shops.length === 0 ? (
        <Empty title="No chestshops found for this account" />
      ) : (
        <DataTable
          rows={data.shops}
          rowKey={(row) => String(row.shopId)}
          searchPlaceholder="Search items or worlds"
          pageSize={25}
          initialSort={{ key: 'value', dir: 'desc' }}
          columns={[
            {
              key: 'item',
              header: 'Item',
              searchText: (row) => `${row.itemKey} ${row.material} ${row.world ?? ''}`,
              sortValue: (row) => row.itemKey,
              render: (row) => (
                <span>
                  {row.itemKey}
                  {row.suppressed && (
                    <span className="badge warning" style={{ marginLeft: 6 }}>
                      suppressed
                    </span>
                  )}
                </span>
              ),
            },
            {
              key: 'price',
              header: 'Price',
              align: 'num',
              sortValue: (row) => row.unitPrice,
              render: (row) => (row.unitPrice == null ? '-' : unitMoney(row.unitPrice)),
            },
            {
              key: 'stock',
              header: 'Stock',
              align: 'num',
              sortValue: (row) => row.currentStock,
              render: (row) => number(row.currentStock ?? 0),
            },
            {
              key: 'value',
              header: 'Value',
              align: 'num',
              sortValue: (row) => (row.currentStock ?? 0) * (row.unitPrice ?? 0),
              render: (row) => money((row.currentStock ?? 0) * (row.unitPrice ?? 0)),
            },
            {
              key: 'where',
              header: 'Location',
              sortValue: (row) => row.world,
              render: (row) => (
                <span className="muted small">
                  {row.world ?? '-'}
                  {row.coords ? <div>{row.coords}</div> : null}
                </span>
              ),
            },
          ]}
        />
      )}
    </div>
  );
}

type CoverageResult = ReturnType<typeof useAsync<Awaited<ReturnType<typeof api.admin.coverage>>>>;

/**
 * Coverage is the number that decides whether the product is honest. A
 * benchmark built from two sales by one seller is not a market price, and this
 * table is how an operator sees how much of the catalogue is in that state.
 */
function CoverageCard({ coverage }: { coverage: CoverageResult }) {
  if (coverage.loading) return <Card title="Network coverage"><Spinner /></Card>;
  if (!coverage.data) return null;

  const { stats, summary, thresholds } = coverage.data;

  return (
    <Card
      title="Network coverage"
      subtitle={`Good needs ${thresholds.minSampleSize}+ sales from ${thresholds.minSellerCount}+ distinct sellers`}
      actions={
        <div className="row">
          <Badge tone="good">{summary.good} good</Badge>
          <Badge tone="warning">{summary.thin} thin</Badge>
          <Badge tone="muted">{summary.none} none</Badge>
        </div>
      }
    >
      <DataTable
        rows={stats}
        rowKey={(stat) => stat.itemCode}
        initialSort={{ key: 'sellers', dir: 'desc' }}
        searchPlaceholder="Search items"
        pageSize={40}
        columns={[
          {
            key: 'item',
            header: 'Item',
            searchText: (stat) => `${stat.displayName} ${stat.itemCode}`,
            sortValue: (stat) => stat.displayName,
            render: (stat) => stat.displayName,
          },
          {
            key: 'sellers',
            header: 'Sellers',
            align: 'num',
            sortValue: (stat) => stat.sellerCount,
            render: (stat) => number(stat.sellerCount),
          },
          {
            key: 'events',
            header: 'Sales in window',
            align: 'num',
            sortValue: (stat) => stat.events30d,
            render: (stat) => number(stat.events30d),
          },
          {
            key: 'quality',
            header: 'Quality',
            sortValue: (stat) => ({ good: 3, thin: 2, none: 1 })[stat.quality] ?? 0,
            render: (stat) => (
              <Badge
                tone={stat.quality === 'good' ? 'good' : stat.quality === 'thin' ? 'warning' : 'muted'}
              >
                {stat.quality}
              </Badge>
            ),
          },
        ]}
      />
    </Card>
  );
}

type BaselineResult = ReturnType<typeof useAsync<Awaited<ReturnType<typeof api.market.baselines>>>>;

/**
 * Manual `/find` entry. The in-game GUI has no export and automating the command
 * is not allowed, so this is a person typing what they saw - which is also why
 * it records who entered it and when.
 */
function BaselineCard({ baselines }: { baselines: BaselineResult }) {
  const [itemCode, setItemCode] = useState('DIAMOND');
  const [price, setPrice] = useState('');
  const [stores, setStores] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const parsedPrice = Number(price);
    if (!Number.isFinite(parsedPrice) || parsedPrice <= 0) return;
    setBusy(true);
    try {
      await api.market.addBaseline({
        itemCode,
        averagePrice: parsedPrice,
        ...(Number(stores) > 0 ? { storeCount: Number(stores) } : {}),
      });
      setPrice('');
      setStores('');
      baselines.reload();
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="/find baseline entries"
      subtitle="Recorded by hand from the in-game GUI - never scraped, never automated"
    >
      <div className="row" style={{ marginBottom: 14, alignItems: 'flex-end' }}>
        <div className="field" style={{ marginBottom: 0, minWidth: 200 }}>
          <label htmlFor="baseline-item">Item</label>
          <select id="baseline-item" value={itemCode} onChange={(e) => setItemCode(e.target.value)}>
            {ITEM_CATALOG.map((item) => (
              <option key={item.code} value={item.code}>
                {item.displayName}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ marginBottom: 0, width: 140 }}>
          <label htmlFor="baseline-price">Average price</label>
          <input
            id="baseline-price"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            placeholder="220.00"
          />
        </div>
        <div className="field" style={{ marginBottom: 0, width: 120 }}>
          <label htmlFor="baseline-stores">Stores</label>
          <input
            id="baseline-stores"
            value={stores}
            onChange={(e) => setStores(e.target.value)}
            placeholder="12"
          />
        </div>
        <button className="primary" onClick={submit} disabled={busy || price.trim() === ''}>
          {busy ? 'Saving...' : 'Record'}
        </button>
      </div>

      {baselines.loading ? (
        <Spinner />
      ) : (baselines.data?.observations.length ?? 0) === 0 ? (
        <Empty title="No baselines recorded yet" />
      ) : (
        <DataTable
          rows={baselines.data!.observations}
          rowKey={(observation) => observation.id}
          initialSort={{ key: 'recorded', dir: 'desc' }}
          searchPlaceholder="Search items or districts"
          pageSize={25}
          columns={[
            {
              key: 'item',
              header: 'Item',
              searchText: (o) => `${o.displayName} ${o.itemCode}`,
              sortValue: (o) => o.displayName,
              render: (o) => o.displayName,
            },
            {
              key: 'price',
              header: 'Average price',
              align: 'num',
              sortValue: (o) => o.averagePrice,
              render: (o) => `$${o.averagePrice.toFixed(2)}`,
            },
            {
              key: 'stores',
              header: 'Stores',
              align: 'num',
              sortValue: (o) => o.storeCount,
              render: (o) => o.storeCount ?? '-',
            },
            {
              key: 'district',
              header: 'District',
              searchText: (o) => o.district ?? '',
              sortValue: (o) => o.district,
              render: (o) => <span className="muted">{o.district ?? '-'}</span>,
            },
            {
              key: 'recorded',
              header: 'Recorded',
              align: 'num',
              sortValue: (o) => o.observedAt,
              render: (o) => relativeTime(o.observedAt),
            },
          ]}
        />
      )}
    </Card>
  );
}
